import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowUpRight, Hash, Loader2, Newspaper } from "lucide-react";
import api from "../api";
import { useLanguage } from "../i18n";
import { usePageMeta } from "../hooks/usePageMeta";
import { Reveal } from "../components/Reveal";

const imgSrc = (u) => (u && u.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u);

export default function Blog() {
  usePageMeta("blog.title", "blog.sub");
  const { t, lang } = useLanguage();
  const [posts, setPosts] = useState(null);
  const [cats, setCats] = useState([]);
  const [active, setActive] = useState("");
  const [activeTag, setActiveTag] = useState("");
  const [searchParams, setSearchParams] = useSearchParams();
  const queryTag = searchParams.get("tag") || "";

  useEffect(() => {
    setActiveTag(queryTag);
    if (queryTag) setActive("");
  }, [queryTag]);

  const selectTag = (tag) => {
    setActive("");
    setActiveTag(tag);
    setSearchParams(tag ? { tag } : {});
  };

  const selectCategory = (name) => {
    setActive(active === name ? "" : name);
    setActiveTag("");
    setSearchParams({});
  };

  useEffect(() => {
    api.get(`/blog/categories?lang=${lang}`).then(({ data }) => setCats(data.data || [])).catch(() => {});
  }, [lang]);

  useEffect(() => {
    setPosts(null);
    const q = `${active ? `&category=${encodeURIComponent(active)}` : ""}${activeTag ? `&tag=${encodeURIComponent(activeTag)}` : ""}`;
    api.get(`/blog/posts?lang=${lang}${q}`).then(({ data }) => setPosts(data.data || [])).catch(() => setPosts([]));
  }, [active, activeTag, lang]);

  const fmtDate = (iso) => {
    try { return new Date(iso).toLocaleDateString(lang, { day: "numeric", month: "long", year: "numeric" }); }
    catch { return ""; }
  };

  return (
    <div data-testid="blog-page" className="px-4 sm:px-6 lg:px-12 max-w-6xl mx-auto py-14 sm:py-20">
      <Reveal>
        <div className="max-w-2xl">
          <div className="font-mono-t text-[11px] font-bold uppercase tracking-[0.3em] text-[#FF6B35] mb-4 flex items-center gap-2">
            <Newspaper className="w-4 h-4" /> BLOG
          </div>
          <h1 className="font-display font-extrabold tracking-tight text-4xl sm:text-6xl text-slate-900">{t("blog.title")}</h1>
          <p className="mt-4 text-lg text-slate-600">{t("blog.sub")}</p>
        </div>
      </Reveal>

      {cats.length > 0 && (
        <div className="mt-10 flex flex-wrap gap-2" data-testid="blog-categories">
          <button data-testid="blog-cat-all" onClick={() => { setActive(""); selectTag(""); }}
            className={`rounded-full px-5 py-2 font-mono-t text-[10px] font-bold uppercase tracking-widest transition-colors ${!active && !activeTag ? "bg-[#1E293B] text-white" : "border border-slate-200 text-slate-500 hover:border-[#FF6B35] hover:text-[#FF6B35]"}`}>
            {t("blog.all")}
          </button>
          {cats.map((c) => (
            <button key={c.name} data-testid={`blog-cat-${c.name}`} onClick={() => selectCategory(c.name)}
              className={`rounded-full px-5 py-2 font-mono-t text-[10px] font-bold uppercase tracking-widest transition-colors ${active === c.name ? "bg-[#FF6B35] text-white" : "border border-slate-200 text-slate-500 hover:border-[#FF6B35] hover:text-[#FF6B35]"}`}>
              {c.name} <span className="opacity-60">{c.count}</span>
            </button>
          ))}
          {activeTag && (
            <button data-testid="blog-tag-active" onClick={() => selectTag("")}
              className="rounded-full px-5 py-2 font-mono-t text-[10px] font-bold uppercase tracking-widest bg-[#1E293B] text-white flex items-center gap-1">
              <Hash className="w-3 h-3" /> {activeTag} ×
            </button>
          )}
        </div>
      )}

      <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
        {posts === null ? (
          <div className="col-span-full py-20 text-center"><Loader2 className="w-7 h-7 animate-spin mx-auto text-[#FF6B35]" /></div>
        ) : posts.length === 0 ? (
          <p data-testid="blog-empty" className="col-span-full py-20 text-center text-slate-400">{t("blog.empty")}</p>
        ) : (
          posts.map((p, i) => (
            <Reveal key={p.post_id} delay={i * 0.06}>
              <div className="group rounded-3xl bg-white border border-slate-200/80 shadow-sm h-full overflow-hidden hover:-translate-y-1 hover:shadow-xl hover:shadow-orange-100 transition-all duration-200">
                <Link to={`/blog/${p.slug}`} data-testid={`blog-card-${p.slug}`} className="block">
                  {p.image && (
                    <div className="h-44 overflow-hidden">
                      <img src={imgSrc(p.image)} alt={p.title} loading="lazy"
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                    </div>
                  )}
                  <div className="p-7 pt-5">
                    <div className="flex flex-wrap gap-1.5 mb-4">
                      {(p.categories || []).map((c) => (
                        <span key={c} className="rounded-full bg-[#FAF5EE] px-3 py-1 font-mono-t text-[9px] font-bold uppercase tracking-widest text-[#FF6B35]">{c}</span>
                      ))}
                    </div>
                    <h2 className="font-display font-bold text-xl text-slate-900 group-hover:text-[#FF6B35] transition-colors">{p.title}</h2>
                    <p className="mt-2 text-sm text-slate-600 leading-relaxed line-clamp-3">{p.excerpt}</p>
                    <div className="mt-5 flex items-center justify-between">
                      <span className="font-mono-t text-[10px] text-slate-400">{fmtDate(p.publish_at || p.created_at)}</span>
                      <ArrowUpRight className="w-4 h-4 text-slate-300 group-hover:text-[#FF6B35] group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all" />
                    </div>
                  </div>
                </Link>
                {(p.tags || []).length > 0 && (
                  <div className="px-7 pb-5 -mt-1 flex flex-wrap gap-1.5">
                    {p.tags.map((tag) => (
                      <button key={tag} data-testid={`blog-tag-${p.slug}-${tag}`} onClick={() => selectTag(tag)}
                        className="inline-flex items-center gap-0.5 rounded-full border border-slate-200 px-2.5 py-0.5 font-mono-t text-[9px] uppercase tracking-widest text-slate-400 hover:border-[#FF6B35] hover:text-[#FF6B35] transition-colors">
                        <Hash className="w-2.5 h-2.5" />{tag}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </Reveal>
          ))
        )}
      </div>
    </div>
  );
}
